import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { payloadAPI } from '@/lib/payload'
import { formatCurrency, formatPercentage, debounce } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { AlgorithmCard } from '@/components/marketplace/algorithm-card'
import { SearchFilter } from '@/components/marketplace/search-filter'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Star, TrendingUp, Filter, Grid, List, Search } from 'lucide-react'

export const Route = createFileRoute('/marketplace')({
  component: MarketplaceComponent,
})

function MarketplaceComponent() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    category: '',
    developer: '',
    minPrice: 0,
    maxPrice: 10000,
    rating: 0,
    featured: false,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [page, setPage] = useState(1)
  const limit = 12

  const { data: algorithms, isLoading, error } = useQuery({
    queryKey: ['algorithms', 'list', { page, limit, searchQuery, filters }],
    queryFn: () => payloadAPI.getAlgorithms({
      page,
      limit,
      category: filters.category || undefined,
      developer: filters.developer || undefined,
      minPrice: filters.minPrice || undefined,
      maxPrice: filters.maxPrice || undefined,
      featured: filters.featured || undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }),
  })

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => payloadAPI.getCategories(),
    retry: false, // Don't retry 404s
  })

  const { data: developers } = useQuery({
    queryKey: ['developers'],
    queryFn: () => payloadAPI.getDevelopers({ verified: true }),
    retry: false, // Don't retry 404s
  })

  const handleSearch = debounce((query: string) => {
    setSearchQuery(query)
    setPage(1)
  }, 300)

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
    setPage(1)
  }

  const totalPages = algorithms ? Math.ceil(algorithms.totalDocs / limit) : 1

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Futures Algorithm Marketplace
          </h1>
          <p className="text-lg text-muted-foreground">
            Discover powerful futures trading algorithms created by verified developers
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-80">
            <SearchFilter
              categories={categories?.docs || []}
              developers={developers?.docs || []}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search and Controls */}
            <div className="mb-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search algorithms..."
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filters.featured ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange({ featured: !filters.featured })}
                >
                  <Star className="h-4 w-4 mr-1" />
                  Featured
                </Button>
                <Button
                  variant={filters.sortBy === 'rating' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange({ sortBy: 'rating', sortOrder: 'desc' })}
                >
                  Top Rated
                </Button>
                <Button
                  variant={filters.sortBy === 'price' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange({ sortBy: 'price', sortOrder: 'asc' })}
                >
                  Lowest Price
                </Button>
                <Button
                  variant={filters.sortBy === 'downloads' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange({ sortBy: 'downloads', sortOrder: 'desc' })}
                >
                  Most Popular
                </Button>
              </div>
            </div>

            {/* Results */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-destructive">Failed to load algorithms</p>
              </div>
            ) : (algorithms?.docs?.length ?? 0) === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No algorithms found matching your criteria</p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-muted-foreground">
                    Showing {algorithms?.docs?.length ?? 0} of {algorithms?.totalDocs ?? 0} algorithms
                  </p>
                </div>

                {/* Algorithm Grid/List */}
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
                  : 'space-y-4'
                }>
                  {(algorithms?.docs ?? []).map((algorithm: any) => (
                    <AlgorithmCard
                      key={algorithm.id}
                      algorithm={algorithm}
                      viewMode={viewMode}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center space-x-2">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        )
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}